import { FC, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, TreeSelect, Select, MultiSelect } from '@maxtropy/components';
import {
  apiV2DistributionCabinetAddPost,
  apiV2DistributionCabinetEditPost,
  apiV2OuListOwnedPost,
  V2OuListOwnedPostResponse,
  apiV2DistributionCabinetRoomCabinetTreePost,
} from '@maxtropy/device-customer-apis-v2';
import { NodeType, RoomItem } from '../../utils';

interface AddCabinetModalProps {
  onClose: () => void;
  updateFn: () => void;
  visible: boolean;
  cabinet?: any;
}
export type OuInfoProps = Exclude<V2OuListOwnedPostResponse['list'], undefined>[number];
const AddCabinetModal: FC<AddCabinetModalProps> = ({ cabinet, onClose, updateFn, visible }) => {
  const [form] = Form.useForm();
  const [roomTree, setRoomTree] = useState<RoomItem[]>([]);
  const [treeData, setTreeData] = useState<RoomItem[]>([]);
  const [ouItems, setOuItems] = useState<OuInfoProps[]>([]);
  const isRoomId = Form.useWatch('roomId', form);

  useEffect(() => {
    apiV2DistributionCabinetRoomCabinetTreePost({}).then(res => {
      setTreeData(res.list ?? []);
      const filterNodesByType = (nodes: RoomItem[]): RoomItem[] => {
        if (!nodes) return [];

        return nodes
          .filter(node => node.type === NodeType.ROOM) // 只保留type=0的节点
          .map(node => ({
            ...node,
            title: node.name,
            value: node.id,
            children: filterNodesByType(node.children ?? []), // 递归处理子节点
          }));
      };

      const filteredData = filterNodesByType(res.list ?? []);
      setRoomTree(filteredData);
    });
    // 查询 ou
    apiV2OuListOwnedPost({}).then(res => {
      setOuItems(res?.list ?? []);
    });
  }, []);

  useEffect(() => {
    if (cabinet && treeData.length > 0) {
      // 递归查找包含目标配电柜的配电房
      const findParentRoom = (nodes: RoomItem[]): RoomItem | undefined => {
        for (const node of nodes) {
          // 检查当前节点是否包含目标配电柜
          if (node.children?.some(child => child.id === cabinet.id && child.type === 1)) {
            return node;
          }

          // 递归检查子节点
          if (node.children && node.children.length > 0) {
            const found = findParentRoom(node.children);
            if (found) return found;
          }
        }
        return undefined;
      };

      // 查找父配电房
      const parentRoom = findParentRoom(treeData);

      // 设置表单值
      form.setFieldsValue({
        roomId: parentRoom?.id || undefined, // 如果找到父配电房则设置其ID，否则设为undefined
        cabinetName: cabinet?.name,
        ouIdList: cabinet?.ouIdList,
      });
    }
  }, [cabinet, treeData]);

  const modalTitle = useMemo(() => {
    return !cabinet ? '新建配电柜' : '编辑配电柜';
  }, [cabinet]);

  // 弹框确认
  const onOk = () => {
    form.validateFields().then(values => {
      // 校验回路名是否重复
      if (!cabinet) {
        apiV2DistributionCabinetAddPost({
          name: values.cabinetName,
          roomId: values?.roomId,
          ouIdList: values?.ouIdList,
        }).then(() => {
          updateFn();
          onClose();
        });
      } else {
        apiV2DistributionCabinetEditPost({
          id: cabinet.id,
          name: values.cabinetName,
          roomId: values.roomId,
          ouIdList: values?.ouIdList ?? [],
        }).then(() => {
          updateFn();
          onClose();
        });
      }
    });
  };

  const getNodeNames = (roomId?: number) => {
    // 1. 查找所有需要检查的节点
    let nodesToCheck: RoomItem[] = [];

    if (roomId) {
      // 如果选择了站房，查找该站房(type=0)的直接子节点(type=1)
      const findRoomNode = (nodes: RoomItem[]): RoomItem | undefined => {
        for (const node of nodes) {
          if (node.id === roomId && node.type === NodeType.ROOM) {
            return node;
          }
          if (node.children) {
            const found = findRoomNode(node.children);
            if (found) return found;
          }
        }
        return undefined;
      };

      const roomNode = findRoomNode(treeData);
      if (roomNode) {
        nodesToCheck = roomNode.children ?? [];
      }
    } else {
      // 如果没有选择站房，查找所有第一层type=1的节点
      nodesToCheck = treeData;
    }
    return nodesToCheck;
  };

  return (
    <Modal title={modalTitle} open={visible} onOk={onOk} contentClassName="modal-form-content" onCancel={onClose}>
      <Form form={form}>
        <Form.Item label="所属站房" name="roomId">
          <TreeSelect
            treeData={roomTree}
            allowClear
            treeDefaultExpandAll
            placeholder="请选择"
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          />
        </Form.Item>
        <Form.Item
          label="配电柜名称"
          rules={[
            { required: true, message: '请输入配电柜名称' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                let roomId = getFieldValue('roomId');
                const nodes = getNodeNames(roomId);
                const hasDuplicate = nodes.some(node => node.type === NodeType.CABINET && node.name === value.trim());
                if (!cabinet) {
                  if (hasDuplicate) {
                    return Promise.reject('配电柜名称已存在');
                  }
                } else {
                  if (hasDuplicate && value.trim() !== cabinet.name) {
                    return Promise.reject('配电柜名称已存在');
                  }
                }
                return Promise.resolve();
              },
            }),
          ]}
          name="cabinetName"
        >
          <Input placeholder="请输入名称" />
        </Form.Item>
        {!isRoomId && (
          <Form.Item label="所属运营单元" name="ouIdList" rules={[{ required: true }]}>
            <MultiSelect
              placeholder="请选择"
              style={{ width: '100%' }}
              showSearch
              options={ouItems.map(o => ({ label: o.name, value: o.id }))}
            ></MultiSelect>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AddCabinetModal;
